/**
 * Арифметические помощники для экономики.
 *
 * Назначение файла: держать в одном месте те мелочи, из-за которых обычно и появляются
 * дыры в балансе — округление в пользу игрока, деление суммы на доли с потерей остатка,
 * выход множителя за границы.
 *
 * Импортов из других модулей нет: чистая арифметика.
 *
 * Источник правил: docs/economy/06-formulas.md §Э6.1
 */

import { type Milli } from "./types.js";

/** Ограничение значения диапазоном [min, max] (Э6.1). */
export function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) {
    throw new Error("clamp получил NaN: где-то в множителях потерялось число");
  }
  return Math.min(Math.max(value, min), max);
}

/**
 * Округление суммы вниз до целого milli.
 * Всё, что округлилось, не достаётся игроку: остаток либо остаётся в казне соты,
 * либо уходит в burn (Э6.1).
 */
export function floorMilli(value: number): Milli {
  return Math.floor(value);
}

/**
 * Деление суммы на доли без потери и без появления кристаллов.
 *
 * Проблема, которую решает: `floor(100 × 0.7) + floor(100 × 0.15) + floor(100 × 0.15) = 99`,
 * то есть один milli исчезает. Инвариант 4 из Э6.11 требует, чтобы сумма частей
 * была равна исходной сумме ровно. Поэтому все доли округляются вниз, а остаток
 * целиком отдаётся доле `remainderTo` — по документации это всегда `burn`.
 *
 * @param amount сумма к разделению, milli
 * @param shares доли, в сумме дающие 1.0
 * @param remainderTo ключ доли, куда уходит остаток от округления
 *
 * NoInfer у remainderTo нужен, чтобы набор ключей выводился только из shares:
 * иначе TypeScript сужал результат до одного переданного ключа остатка.
 */
export function splitAmount<K extends string>(
  amount: Milli,
  shares: Readonly<Record<K, number>>,
  remainderTo: NoInfer<K>,
): Record<K, Milli> {
  if (!Number.isInteger(amount) || amount < 0) {
    throw new Error(`splitAmount ожидает неотрицательное целое milli, получено ${amount}`);
  }

  const keys = Object.keys(shares) as K[];
  const remainderKey: K = remainderTo;
  const sumOfShares = keys.reduce((acc, key) => acc + shares[key], 0);
  if (Math.abs(sumOfShares - 1) > 1e-9) {
    throw new Error(`Доли должны давать в сумме 1.0, получено ${sumOfShares}`);
  }
  if (!keys.includes(remainderKey)) {
    throw new Error(`Доля ${remainderKey} для остатка отсутствует в списке долей`);
  }

  const result = {} as Record<K, Milli>;
  let distributed = 0;
  for (const key of keys) {
    if (key === remainderKey) continue;
    const part = Math.floor(amount * shares[key]);
    result[key] = part;
    distributed += part;
  }
  result[remainderKey] = amount - distributed;

  return result;
}

/**
 * Ищет множитель в таблице пороговых значений (например, затухание дохода по суткам
 * без входа). Таблица обязана быть отсортирована по возрастанию порога, а последний
 * порог — бесконечным, иначе значение вне таблицы осталось бы без множителя.
 */
export function lookupStep(
  table: ReadonlyArray<{ maxDays: number; multiplier: number }>,
  days: number,
): number {
  for (const row of table) {
    if (days <= row.maxDays) return row.multiplier;
  }
  throw new Error("Таблица порогов не покрывает переданное значение: нужен ряд с Infinity");
}

/**
 * Берёт значение из массива множителей по индексу, а всё, что выходит за границы,
 * прижимает к последнему элементу. Нужно для множителя навязчивости: шестая осада
 * за сутки стоит столько же, сколько пятая, а не падает в undefined.
 */
export function lastOrClamp(table: ReadonlyArray<number>, index: number): number {
  if (table.length === 0) throw new Error("Пустая таблица множителей");
  const safeIndex = clamp(Math.trunc(index), 0, table.length - 1);
  const value = table[safeIndex];
  if (value === undefined) throw new Error("Индекс вне таблицы множителей");
  return value;
}
